import React, { useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * ImageDropzone — ช่องอัปโหลดภาพที่บอกข้อกำหนดขนาดในหน้าจอ ไม่ใช่ในเอกสาร
 *
 * เหตุผลที่ต้องมีเป็น component กลาง: สามหน้าจอในกลุ่มที่ 3 อัปโหลดภาพ
 * (สิทธิประโยชน์ Tier · ของรางวัล · แบนเนอร์) และแต่ละที่มีข้อกำหนดขนาดต่างกัน
 * ถ้าเขียนแยกกัน ข้อกำหนดจะเขียนคนละแบบและคนอัปโหลดจะเดาเอง
 *
 * สิ่งที่ component นี้บังคับ:
 *   — บอกอัตราส่วน ขนาดต่ำสุด และขนาดไฟล์สูงสุด **ก่อน** เลือกไฟล์ ไม่ใช่หลังอัปโหลดล้มเหลว
 *   — ตรวจอัตราส่วนจริงจากไฟล์ที่เลือก และบอกค่าที่ได้เทียบกับค่าที่ต้องการ
 *   — บังคับกรอกข้อความแทนภาพ (alt) เพราะภาพเหล่านี้ขึ้นบนแอปของลูกค้า
 *
 * ไฟล์นี้ไม่อัปโหลดจริง — ใช้ URL.createObjectURL เพื่อพรีวิวในขั้นออกแบบ
 * การต่อ API อยู่ใน ADMIN-API-REQUIREMENTS (endpoint อัปโหลดสื่อ)
 */

export interface ImageSpec {
  /** อัตราส่วนที่ต้องการ เช่น '16:9' */
  aspect: string;
  /** ความกว้างต่ำสุด (px) — ต่ำกว่านี้ภาพจะแตกบนจอความละเอียดสูง */
  minWidth: number;
  /** ขนาดไฟล์สูงสุด (KB) */
  maxKB: number;
  formats?: string[];
  /** จะไปแสดงที่ไหนในแอป — ทำให้คนอัปโหลดรู้ว่าภาพจะถูกครอปแบบไหน */
  usedAt?: string;
}

export interface ImageValue {
  url: string;
  alt: string;
  /** ชื่อไฟล์ที่อัปโหลด — แสดงให้รู้ว่าไฟล์ไหน */
  fileName?: string;
  /**
   * ไฟล์จริงที่ผู้ใช้เลือก — ชั้นข้อมูลเอาไปอัปโหลดต่อ
   * ไม่มีค่า = รูปที่โหลดมาจาก backend อยู่แล้ว ไม่ต้องอัปโหลดซ้ำ
   *
   * ⚠️ **ผู้เรียกต้องส่งค่านี้กลับเข้ามาใน `value` ด้วย** ไม่งั้นไฟล์จะหาย
   *    ตอนผู้ใช้แก้คำอธิบายภาพ เพราะตรงนั้นส่งค่าเดิมกลับออกไปทั้งก้อน
   */
  file?: File;
  width?: number;
  height?: number;
  sizeKB?: number;
}

const ratioOf = (spec: string): number => {
  const [w, h] = spec.split(':').map(Number);
  return w && h ? w / h : 1;
};

const fmtRatio = (w: number, h: number): string => {
  const r = w / h;
  const known: Array<[string, number]> = [['16:9', 16 / 9], ['4:3', 4 / 3], ['1:1', 1], ['3:2', 3 / 2], ['9:16', 9 / 16]];
  const hit = known.find(([, v]) => Math.abs(v - r) < 0.02);
  return hit ? hit[0] : `${r.toFixed(2)}:1`;
};

let idSeq = 0;

export interface ImageDropzoneProps {
  value: ImageValue | null;
  onChange: (v: ImageValue | null) => void;
  spec: ImageSpec;
  label?: string;
  /** บังคับกรอกข้อความแทนภาพ (ค่าตั้งต้น: บังคับ) */
  requireAlt?: boolean;
  /** ความกว้างของช่องพรีวิว (px) */
  previewWidth?: number;
  disabled?: boolean;
  className?: string;
}

export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  value, onChange, spec, label = 'ภาพประกอบ', requireAlt = true, previewWidth = 320, disabled, className,
}) => {
  /**
   * React 16 ไม่มี useId — ต้องสร้างเอง
   * (useId เป็นของ React 18 ส่วน host ใช้ 16.14.0)
   * useRef ทำให้ค่าคงที่ตลอดอายุของ component ซึ่งเป็นสิ่งเดียวที่ label/input ต้องการ
   */
  const inputId = useRef(`bo-drop-${(idSeq += 1)}`).current;
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const wanted = ratioOf(spec.aspect);
  const previewHeight = Math.round(previewWidth / wanted);
  const formats = spec.formats ?? ['JPG', 'PNG', 'WebP'];

  const accept = (file: File) => {
    setProblem(null);
    const sizeKB = Math.round(file.size / 1024);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const notes: string[] = [];
      const got = img.naturalWidth / img.naturalHeight;
      if (Math.abs(got - wanted) > 0.02) {
        notes.push(`อัตราส่วนที่ได้คือ ${fmtRatio(img.naturalWidth, img.naturalHeight)} แต่ต้องเป็น ${spec.aspect} — ภาพจะถูกครอปบนแอป`);
      }
      if (img.naturalWidth < spec.minWidth) {
        notes.push(`ความกว้าง ${img.naturalWidth}px ต่ำกว่าค่าต่ำสุด ${spec.minWidth}px — ภาพจะแตกบนจอความละเอียดสูง`);
      }
      if (sizeKB > spec.maxKB) {
        notes.push(`ไฟล์ ${sizeKB.toLocaleString('th-TH')} KB เกินเพดาน ${spec.maxKB.toLocaleString('th-TH')} KB — ต้องบีบอัดก่อน`);
      }
      setProblem(notes.length ? notes.join(' · ') : null);
      onChange({
        url, alt: value?.alt ?? '', fileName: file.name, file,
        width: img.naturalWidth, height: img.naturalHeight, sizeKB,
      });
    };
    img.onerror = () => setProblem('อ่านไฟล์ภาพนี้ไม่ได้ — ต้องเป็นไฟล์ ' + formats.join(' / '));
    img.src = url;
  };

  const specLine = (
    <span className="flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-700)]">
      <span className="flex items-center gap-1"><Icon name="aspect_ratio" size={14} />อัตราส่วน <strong className="bo-num" style={{ textAlign: 'left' }}>{spec.aspect}</strong></span>
      <span className="flex items-center gap-1"><Icon name="straighten" size={14} />กว้างอย่างน้อย <strong className="bo-num" style={{ textAlign: 'left' }}>{spec.minWidth.toLocaleString('th-TH')}px</strong></span>
      <span className="flex items-center gap-1"><Icon name="database" size={14} />ไม่เกิน <strong className="bo-num" style={{ textAlign: 'left' }}>{spec.maxKB.toLocaleString('th-TH')} KB</strong></span>
      <span className="flex items-center gap-1"><Icon name="image" size={14} />{formats.join(' · ')}</span>
    </span>
  );

  return (
    <div className={cn('flex flex-col gap-[var(--space-2)]', className)}>
      <div className="flex flex-col gap-[var(--space-05)]">
        <span className="text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">{label}</span>
        {specLine}
        {spec.usedAt && (
          <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
            ใช้ที่: {spec.usedAt}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-[var(--space-3)]">
        {value ? (
          <figure className="m-0 flex flex-col gap-[var(--space-1)]">
            <img
              src={value.url}
              alt={value.alt || 'ภาพที่อัปโหลด — ยังไม่ได้กรอกข้อความแทนภาพ'}
              style={{ width: previewWidth, height: previewHeight, objectFit: 'cover' }}
              className="rounded-[var(--radius-md)] border border-[var(--line-300)] bg-[var(--surface-100)]"
            />
            <figcaption className="flex flex-wrap items-center gap-x-[var(--space-2)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
              {value.fileName && <span className="bo-truncate max-w-[220px]">{value.fileName}</span>}
              {value.width && (
                <span className="bo-num" style={{ textAlign: 'left' }}>
                  {value.width}×{value.height}px
                </span>
              )}
              {value.sizeKB !== undefined && (
                <span className="bo-num" style={{ textAlign: 'left' }}>{value.sizeKB.toLocaleString('th-TH')} KB</span>
              )}
            </figcaption>
          </figure>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f && !disabled) accept(f);
            }}
            style={{ width: previewWidth, height: previewHeight }}
            className={cn(
              'flex flex-col items-center justify-center gap-[var(--space-1)] rounded-[var(--radius-md)] border-2 border-dashed p-[var(--space-3)] text-center',
              dragging ? 'border-[var(--blue-700)] bg-[var(--blue-050)]' : 'border-[var(--line-300)] bg-[var(--surface-100)]',
            )}
          >
            <Icon name="add_photo_alternate" size={28} className="text-[var(--ink-600)]" />
            <span className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
              ลากไฟล์มาวาง หรือกดเลือกไฟล์
            </span>
            <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-600)]" style={{ textAlign: 'center' }}>
              {spec.aspect} · {spec.minWidth}px ขึ้นไป
            </span>
          </div>
        )}

        <div className="flex min-w-[240px] flex-1 flex-col gap-[var(--space-2)]">
          <input
            ref={fileRef}
            id={inputId}
            type="file"
            accept="image/*"
            disabled={disabled}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) accept(f); }}
            className="sr-only"
          />
          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <button
              type="button"
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
              className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] disabled:cursor-not-allowed disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <Icon name="upload" size={16} />
              {value ? 'เปลี่ยนภาพ' : 'เลือกไฟล์'}
            </button>
            {value && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => { onChange(null); setProblem(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--error-text)] hover:bg-[var(--error-bg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                <Icon name="delete" size={16} />
                เอาภาพออก
              </button>
            )}
          </div>

          {value && (
            <label className="flex flex-col gap-[var(--space-05)]">
              <span className="flex items-center gap-1 text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">
                ข้อความแทนภาพ
                {requireAlt && <span aria-hidden="true" className="text-[var(--error-text)]">*</span>}
              </span>
              <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
                โปรแกรมอ่านหน้าจอจะอ่านข้อความนี้แทนภาพ — เขียนสิ่งที่ภาพบอก ไม่ใช่คำว่า &quot;ภาพแบนเนอร์&quot;
              </span>
              <input
                type="text"
                value={value.alt}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, alt: e.target.value })}
                className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              />
              {requireAlt && !value.alt.trim() && (
                <span role="alert" className="flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
                  <Icon name="error" size={14} filled />
                  ยังไม่ได้กรอก — บันทึกไม่ได้จนกว่าจะกรอก
                </span>
              )}
            </label>
          )}

          {problem && (
            <p role="alert" className="m-0 flex items-start gap-[var(--space-15)] rounded-[var(--radius-sm)] bg-[var(--warning-bg)] p-[var(--space-2)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
              <Icon name="warning" size={17} filled className="mt-[var(--space-05)] shrink-0 text-[var(--warning-text)]" />
              {problem}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
